import { useWorkspace } from "@/lib/WorkspaceContext";
import {
  ArrowRightLeft,
  Building2,
  Check,
  ChevronDown,
  CreditCard,
  Home,
  Landmark,
  Loader2,
  Package,
  Plus,
  Search,
  Settings2,
  TrendingUp,
  Wallet,
  X,
} from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import TransferHoldingsModal from "@/components/TransferHoldingsModal";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from "@/components/ui/sheet";
import { toast } from "sonner";
import { authFetch } from "@/lib/api";

const API = "";

const TYPE_CONFIG = {
  brokerage: { label: "Broker", icon: TrendingUp, color: "text-blue-400 bg-blue-500/10 border-blue-500/20" },
  fund: { label: "Fund", icon: Package, color: "text-purple-400 bg-purple-500/10 border-purple-500/20" },
  bank: { label: "Bank", icon: Landmark, color: "text-emerald-400 bg-emerald-500/10 border-emerald-500/20" },
  wallet: { label: "Wallet", icon: Wallet, color: "text-amber-400 bg-amber-500/10 border-amber-500/20" },
  crypto: { label: "Crypto", icon: CreditCard, color: "text-orange-400 bg-orange-500/10 border-orange-500/20" },
  property: { label: "Property", icon: Home, color: "text-teal-400 bg-teal-500/10 border-teal-500/20" },
  liability: { label: "Liability", icon: CreditCard, color: "text-red-400 bg-red-500/10 border-red-500/20" },
  cash: { label: "Cash", icon: Wallet, color: "text-emerald-400 bg-emerald-500/10 border-emerald-500/20" },
  retirement: { label: "Retirement", icon: Landmark, color: "text-sky-400 bg-sky-500/10 border-sky-500/20" },
  manual: { label: "Manual", icon: Building2, color: "text-muted-foreground bg-muted/30 border-border" },
  paper: { label: "Paper", icon: Building2, color: "text-muted-foreground bg-muted/30 border-border" },
  other: { label: "Other", icon: Building2, color: "text-muted-foreground bg-muted/30 border-border" },
};

function getInstitutionKey(account) {
  return account?.institution_id || account?.institution_name || "unassigned";
}

function getInstitutionLabel(account) {
  return account?.institution_name || "Unassigned Institution";
}

function InstitutionLogo({ logo, name, size = 18 }) {
  const [failed, setFailed] = useState(false);

  if (!logo || failed) {
    const initials = (name || "UI")
      .split(/\s+/)
      .slice(0, 2)
      .map((part) => part[0])
      .join("")
      .toUpperCase();

    return (
      <div
        className="rounded-md bg-muted/40 flex items-center justify-center shrink-0 border border-border/40"
        style={{ width: size, height: size }}
      >
        <span className="text-[9px] font-bold text-muted-foreground">{initials || "UI"}</span>
      </div>
    );
  }

  return (
    <img
      src={logo}
      alt=""
      className="rounded-md object-contain shrink-0 border border-border/20 bg-background"
      style={{ width: size, height: size }}
      onError={() => setFailed(true)}
    />
  );
}

function AccountTypeBadge({ type }) {
  const cfg = TYPE_CONFIG[type] || TYPE_CONFIG.other;
  return (
    <Badge variant="outline" className={`text-[9px] ${cfg.color}`}>
      {cfg.label}
    </Badge>
  );
}

function CreateAccountModal({ open, onClose, workspaceId, onCreated }) {
  const [name, setName] = useState("");
  const [type, setType] = useState("brokerage");
  const [search, setSearch] = useState("");
  const [institutions, setInstitutions] = useState([]);
  const [selected, setSelected] = useState(null);
  const [creating, setCreating] = useState(false);

  useEffect(() => {
    if (!open) return;
    authFetch(`${API}/api/institutions`)
      .then((response) => response.json())
      .then((data) => setInstitutions(data.institutions || []))
      .catch(() => {});
  }, [open]);

  const filtered = useMemo(() => {
    const typeMap = {
      brokerage: "broker",
      fund: "fund",
      bank: "bank",
      crypto: "crypto",
      wallet: "bank",
      cash: "bank",
      retirement: "broker",
    };
    const institutionType = typeMap[type];

    return institutions.filter((institution) => {
      if (institutionType && institution.type !== institutionType) return false;
      if (!search.trim()) return true;
      const query = search.trim().toLowerCase();
      return (
        institution.name.toLowerCase().includes(query) ||
        institution.aliases?.some((alias) => alias.toLowerCase().includes(query))
      );
    });
  }, [institutions, search, type]);

  const reset = () => {
    setName("");
    setType("brokerage");
    setSearch("");
    setSelected(null);
  };

  const handleCreate = async () => {
    if (!workspaceId || !name.trim()) return;
    setCreating(true);
    try {
      const res = await authFetch(`${API}/api/workspaces/${workspaceId}/accounts`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          name: name.trim(),
          type,
          institution_id: selected?.id || null,
          institution_name: selected?.name || null,
          institution_logo: selected?.logo || null,
        }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        toast.error(err.detail || "Failed to create account");
        return;
      }
      const createdAccount = await res.json().catch(() => null);
      await onCreated?.(createdAccount);
      toast.success(`Account "${name.trim()}" created`);
      reset();
      onClose();
    } catch {
      toast.error("Failed to create account");
    } finally {
      setCreating(false);
    }
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-[70] bg-black/70 flex items-end justify-center p-0 sm:items-center sm:p-4" onClick={onClose}>
      <div
        className="w-full rounded-t-2xl border border-border bg-card shadow-2xl sm:max-w-md sm:rounded-2xl"
        onClick={(event) => event.stopPropagation()}
        data-testid="create-account-modal"
      >
        <div className="flex items-center justify-between border-b border-border p-4">
          <div>
            <h3 className="text-sm font-semibold">Create Account</h3>
            <p className="text-xs text-muted-foreground mt-1">Attach an institution when you have one so switching stays clear later.</p>
          </div>
          <button onClick={onClose} className="text-muted-foreground hover:text-foreground">
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="max-h-[72dvh] overflow-y-auto space-y-4 p-4">
          <div>
            <label className="text-[11px] font-medium text-muted-foreground mb-1.5 block">Account Type</label>
            <div className="flex flex-wrap gap-1.5">
              {Object.keys(TYPE_CONFIG)
                .filter((entry) => !["manual", "paper", "other"].includes(entry))
                .map((entry) => {
                  const cfg = TYPE_CONFIG[entry];
                  const Icon = cfg.icon;
                  return (
                    <button
                      key={entry}
                      onClick={() => {
                        setType(entry);
                        setSelected(null);
                      }}
                      className={`flex items-center gap-1 px-2.5 py-1.5 rounded-lg text-[10px] font-medium border transition-all ${
                        type === entry ? `${cfg.color} ring-1 ring-current/30` : "text-muted-foreground bg-muted/10 border-border/30 hover:border-border"
                      }`}
                    >
                      <Icon className="w-3 h-3" />
                      {cfg.label}
                    </button>
                  );
                })}
            </div>
          </div>

          <div>
            <label className="text-[11px] font-medium text-muted-foreground mb-1.5 block">Institution</label>
            <div className="relative">
              <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-muted-foreground" />
              <input
                value={search}
                onChange={(event) => setSearch(event.target.value)}
                placeholder="Search institution..."
                className="w-full pl-8 pr-3 py-2 bg-muted/20 border border-border rounded-lg text-xs placeholder:text-muted-foreground focus:outline-none focus:ring-1 focus:ring-primary"
              />
            </div>
            {selected && (
              <div className="flex items-center gap-2 mt-2 p-2 rounded-lg bg-primary/5 border border-primary/20">
                <InstitutionLogo logo={selected.logo} name={selected.name} size={20} />
                <span className="text-xs font-medium">{selected.name}</span>
                <button onClick={() => setSelected(null)} className="ml-auto">
                  <X className="w-3 h-3 text-muted-foreground" />
                </button>
              </div>
            )}
            {!selected && (
              <div className="mt-1.5 max-h-[180px] overflow-y-auto space-y-0.5 border border-border/30 rounded-lg">
                {filtered.slice(0, 14).map((institution) => (
                  <button
                    key={institution.id}
                    onClick={() => {
                      setSelected(institution);
                      if (!name) setName(institution.name);
                    }}
                    className="flex items-center gap-2 w-full px-2.5 py-1.5 text-xs hover:bg-muted/30 transition-colors"
                  >
                    <InstitutionLogo logo={institution.logo} name={institution.name} size={18} />
                    <span>{institution.name}</span>
                    <Badge variant="outline" className="text-[8px] ml-auto">
                      {institution.country}
                    </Badge>
                  </button>
                ))}
                {filtered.length === 0 && (
                  <p className="text-[10px] text-muted-foreground text-center py-3">No matches. You can still create the account without institution metadata.</p>
                )}
              </div>
            )}
          </div>

          <div>
            <label className="text-[11px] font-medium text-muted-foreground mb-1.5 block">Display Name</label>
            <input
              value={name}
              onChange={(event) => setName(event.target.value)}
              placeholder="e.g. Webull Core, SCB Savings"
              className="w-full px-3 py-2 bg-muted/20 border border-border rounded-lg text-xs placeholder:text-muted-foreground focus:outline-none focus:ring-1 focus:ring-primary"
              onKeyDown={(event) => event.key === "Enter" && handleCreate()}
            />
          </div>
        </div>

        <div className="flex items-center justify-end gap-2 border-t border-border p-4">
          <button onClick={onClose} className="px-3 py-1.5 text-xs text-muted-foreground hover:text-foreground">
            Cancel
          </button>
          <button
            onClick={handleCreate}
            disabled={!name.trim() || creating || !workspaceId}
            className="flex items-center gap-1.5 px-4 py-1.5 bg-primary text-primary-foreground rounded-lg text-xs font-medium disabled:opacity-50"
          >
            {creating ? <Loader2 className="w-3 h-3 animate-spin" /> : <Plus className="w-3 h-3" />}
            Create Account
          </button>
        </div>
      </div>
    </div>
  );
}

export default function AccountSelector({ className = "", compact = false, triggerVariant }) {
  const navigate = useNavigate();
  const { scope, workspaces, accounts, switchAccount, switchWorkspace, loading, refreshAll } = useWorkspace();
  const [open, setOpen] = useState(false);
  const [showTransfer, setShowTransfer] = useState(false);
  const [showCreate, setShowCreate] = useState(false);
  const [search, setSearch] = useState("");
  const [workspaceAccounts, setWorkspaceAccounts] = useState({});
  const [workspaceLoading, setWorkspaceLoading] = useState(false);
  const [selectedWorkspaceId, setSelectedWorkspaceId] = useState("");
  const [isMobile, setIsMobile] = useState(typeof window !== "undefined" ? window.innerWidth < 768 : false);
  const [switching, setSwitching] = useState(null);

  useEffect(() => {
    if (typeof window === "undefined") return undefined;
    const onResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  useEffect(() => {
    if (scope?.workspace_id) {
      setSelectedWorkspaceId((current) => current || scope.workspace_id);
    }
  }, [scope?.workspace_id]);

  useEffect(() => {
    if (!open) return;
    let cancelled = false;

    const loadWorkspaceAccounts = async () => {
      setWorkspaceLoading(true);
      try {
        const results = await Promise.all(
          workspaces.map(async (workspace) => {
            if (workspace.workspace_id === scope?.workspace_id) {
              return [workspace.workspace_id, accounts];
            }
            const res = await authFetch(`${API}/api/workspaces/${workspace.workspace_id}/accounts`);
            const data = res.ok ? await res.json() : { accounts: [] };
            return [workspace.workspace_id, data.accounts || []];
          })
        );

        if (!cancelled) {
          setWorkspaceAccounts(Object.fromEntries(results));
        }
      } catch {
        if (!cancelled) {
          toast.error("Failed to load workspace accounts");
        }
      } finally {
        if (!cancelled) {
          setWorkspaceLoading(false);
        }
      }
    };

    loadWorkspaceAccounts();
    return () => {
      cancelled = true;
    };
  }, [open, workspaces, scope?.workspace_id, accounts]);

  const activeWorkspace = workspaces.find((workspace) => workspace.workspace_id === scope?.workspace_id);
  const activeAccount = accounts.find((account) => account.account_id === scope?.account_id);
  const activeType = TYPE_CONFIG[activeAccount?.type] || TYPE_CONFIG.other;
  const ActiveIcon = activeType.icon;

  const effectiveWorkspaceId = selectedWorkspaceId || scope?.workspace_id || "";
  const selectedWorkspace = workspaces.find((workspace) => workspace.workspace_id === effectiveWorkspaceId) || activeWorkspace;
  const selectedAccounts = useMemo(() => {
    if (!effectiveWorkspaceId) return [];
    const workspaceScopedAccounts = workspaceAccounts[effectiveWorkspaceId];
    if (workspaceScopedAccounts) return workspaceScopedAccounts;
    return effectiveWorkspaceId === scope?.workspace_id ? accounts : [];
  }, [workspaceAccounts, effectiveWorkspaceId, scope?.workspace_id, accounts]);

  const filteredAccounts = useMemo(() => {
    const query = search.trim().toLowerCase();
    if (!query) return selectedAccounts;
    return selectedAccounts.filter((account) => {
      const institutionLabel = getInstitutionLabel(account).toLowerCase();
      return (
        account.name?.toLowerCase().includes(query) ||
        institutionLabel.includes(query) ||
        account.base_currency?.toLowerCase().includes(query) ||
        account.type?.toLowerCase().includes(query)
      );
    });
  }, [search, selectedAccounts]);

  const groupedInstitutions = useMemo(() => {
    const groups = {};
    filteredAccounts.forEach((account) => {
      const key = getInstitutionKey(account);
      if (!groups[key]) {
        groups[key] = {
          id: key,
          name: getInstitutionLabel(account),
          logo: account.institution_logo || null,
          accounts: [],
        };
      }
      groups[key].accounts.push(account);
    });

    return Object.values(groups).sort((left, right) => {
      if (left.name === "Unassigned Institution") return 1;
      if (right.name === "Unassigned Institution") return -1;
      return left.name.localeCompare(right.name);
    });
  }, [filteredAccounts]);

  const onlyUnassignedInstitutions = useMemo(() => {
    return selectedAccounts.length > 0 && groupedInstitutions.length > 0 && groupedInstitutions.every((group) => group.name === "Unassigned Institution");
  }, [groupedInstitutions, selectedAccounts.length]);

  const workspaceCounts = useMemo(() => {
    const counts = {};
    workspaces.forEach((workspace) => {
      const workspaceList = workspaceAccounts[workspace.workspace_id] || [];
      counts[workspace.workspace_id] = workspaceList.length;
    });
    return counts;
  }, [workspaceAccounts, workspaces]);

  const handleCreateAccount = async (createdAccount) => {
    setSwitching(createdAccount?.account_id || "create-account");
    try {
      if (createdAccount?.workspace_id && createdAccount?.account_id) {
        if (createdAccount.workspace_id === scope?.workspace_id) {
          await switchAccount?.(createdAccount.account_id);
        } else {
          await switchWorkspace?.(createdAccount.workspace_id, createdAccount.account_id);
        }
      } else {
        await refreshAll?.();
      }
      setSelectedWorkspaceId(createdAccount?.workspace_id || scope?.workspace_id || "");
      setSearch("");
    } finally {
      setSwitching(null);
    }
    setOpen(true);
  };

  const handleChooseAccount = async (workspaceId, accountId) => {
    setSwitching(accountId);
    try {
      if (workspaceId === scope?.workspace_id) {
        await switchAccount?.(accountId);
      } else {
        await switchWorkspace?.(workspaceId, accountId);
      }
      setOpen(false);
      setSearch("");
    } finally {
      setSwitching(null);
    }
  };

  const handleSwitchWorkspaceDefault = async () => {
    if (!selectedWorkspaceId) return;
    setSwitching(selectedWorkspaceId);
    try {
      await switchWorkspace?.(selectedWorkspaceId);
      setOpen(false);
      setSearch("");
    } finally {
      setSwitching(null);
    }
  };

  if (loading || !scope) return null;

  const resolvedVariant = triggerVariant || (compact ? "compact" : "full");
  const isIconTrigger = resolvedVariant === "icon";

  const triggerLabel = activeAccount?.name || "Choose account";
  const triggerInstitution = getInstitutionLabel(activeAccount);

  return (
    <>
      <Sheet open={open} onOpenChange={setOpen}>
        <SheetTrigger asChild>
          {isIconTrigger ? (
            <button
              type="button"
              aria-label="Switch workspace/account"
              title="Switch workspace/account"
              className={`flex h-9 w-9 items-center justify-center rounded-xl border border-border/60 bg-card/80 transition-colors hover:bg-muted/40 ${className}`}
              data-testid="account-selector-trigger-icon"
            >
              {activeAccount?.institution_logo ? (
                <InstitutionLogo
                  logo={activeAccount.institution_logo}
                  name={activeAccount.institution_name || triggerLabel}
                  size={22}
                />
              ) : (
                <ActiveIcon className="h-4 w-4 text-primary" />
              )}
            </button>
          ) : (
            <button
              type="button"
              className={`flex items-center gap-2 rounded-xl border border-border/60 bg-card/80 px-3 py-2 text-left transition-colors hover:bg-muted/40 ${className}`}
              data-testid="account-selector-trigger"
            >
              {activeAccount?.institution_logo ? (
                <InstitutionLogo
                  logo={activeAccount.institution_logo}
                  name={activeAccount.institution_name || triggerLabel}
                  size={resolvedVariant === "compact" ? 18 : 20}
                />
              ) : (
                <ActiveIcon className="w-4 h-4 shrink-0 text-primary" />
              )}
              <div className="min-w-0 flex-1">
                <p className={`truncate font-medium ${resolvedVariant === "compact" ? "text-xs" : "text-sm"}`}>{triggerLabel}</p>
                <p className="truncate text-[11px] text-muted-foreground">
                  {activeWorkspace?.name || "Workspace"} - {triggerInstitution}
                </p>
              </div>
              <ChevronDown className="w-3.5 h-3.5 text-muted-foreground shrink-0" />
            </button>
          )}
        </SheetTrigger>

        <SheetContent side={isMobile ? "bottom" : "right"} className={isMobile ? "h-[88dvh] rounded-t-2xl" : "sm:max-w-lg"}>
          <SheetHeader className="pr-8">
            <SheetTitle>Workspace & Account Selector</SheetTitle>
            <SheetDescription>
              Switch scope safely, grouped by workspace and institution. Session updates happen server-side first.
            </SheetDescription>
          </SheetHeader>

          <div className="mt-6 space-y-5">
            <div className="rounded-xl border border-border/60 bg-muted/20 p-4">
              <div className="flex items-center gap-3">
                <InstitutionLogo logo={activeAccount?.institution_logo} name={activeAccount?.institution_name || triggerLabel} size={28} />
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-semibold truncate">{triggerLabel}</p>
                  <p className="text-xs text-muted-foreground truncate">{activeWorkspace?.name || "Workspace"} - {triggerInstitution}</p>
                </div>
                <AccountTypeBadge type={activeAccount?.type} />
              </div>
              <div className="mt-3 flex flex-wrap gap-2 text-[10px] text-muted-foreground">
                <span className="rounded-full border border-border/60 px-2 py-1">{activeAccount?.base_currency || "USD"}</span>
                <span className="rounded-full border border-border/60 px-2 py-1">{accounts.length} account{accounts.length === 1 ? "" : "s"} in workspace</span>
              </div>
            </div>

            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Workspaces</p>
                {workspaceLoading && <Loader2 className="w-3.5 h-3.5 animate-spin text-muted-foreground" />}
              </div>
              {workspaces.length === 0 ? (
                <div className="rounded-xl border border-dashed border-border/60 p-5 text-center">
                  <Building2 className="w-5 h-5 mx-auto text-muted-foreground mb-2" />
                  <p className="text-sm font-medium">No workspaces available</p>
                  <p className="text-xs text-muted-foreground mt-1">
                    Create or restore a workspace first so account scope has somewhere safe to live.
                  </p>
                  <Button variant="outline" size="sm" className="mt-3 gap-2" onClick={() => navigate("/setup")}>
                    <Plus className="w-3.5 h-3.5" />
                    Open setup
                  </Button>
                </div>
              ) : (
                <div className="grid gap-2">
                  {workspaces.map((workspace) => {
                    const isActiveWorkspace = workspace.workspace_id === scope.workspace_id;
                    const isSelectedWorkspace = workspace.workspace_id === selectedWorkspaceId;
                    return (
                      <button
                        key={workspace.workspace_id}
                        onClick={() => setSelectedWorkspaceId(workspace.workspace_id)}
                        className={`rounded-xl border px-3 py-3 text-left transition-colors ${
                          isSelectedWorkspace ? "border-primary/40 bg-primary/5" : "border-border/60 hover:bg-muted/30"
                        }`}
                      >
                        <div className="flex items-center gap-2">
                          <p className="font-medium text-sm truncate">{workspace.name}</p>
                          {isActiveWorkspace && (
                            <Badge variant="outline" className="text-[9px] border-emerald-500/30 text-emerald-400">
                              Active
                            </Badge>
                          )}
                          <span className="ml-auto text-[11px] text-muted-foreground">
                            {workspaceCounts[workspace.workspace_id] || 0} account{workspaceCounts[workspace.workspace_id] === 1 ? "" : "s"}
                          </span>
                        </div>
                        <p className="mt-1 text-[11px] text-muted-foreground">
                          {workspace.role || "member"} role
                        </p>
                      </button>
                    );
                  })}
                </div>
              )}
              {selectedWorkspaceId && selectedWorkspaceId !== scope.workspace_id && (
                <Button variant="outline" size="sm" className="w-full gap-2" onClick={handleSwitchWorkspaceDefault} disabled={switching === selectedWorkspaceId}>
                  {switching === selectedWorkspaceId ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <ArrowRightLeft className="w-3.5 h-3.5" />}
                  Switch to {selectedWorkspace?.name}
                </Button>
              )}
            </div>

            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Accounts</p>
                <div className="flex items-center gap-2">
                  <Button variant="outline" size="sm" className="gap-2" onClick={() => navigate("/account-activity")}>
                    <Settings2 className="w-3.5 h-3.5" />
                    Manage
                  </Button>
                  <Button size="sm" className="gap-2" onClick={() => setShowCreate(true)} disabled={!selectedWorkspaceId}>
                    <Plus className="w-3.5 h-3.5" />
                    Create
                  </Button>
                </div>
              </div>

              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-muted-foreground" />
                <Input
                  value={search}
                  onChange={(event) => setSearch(event.target.value)}
                  placeholder="Search accounts, institutions, currency..."
                  className="pl-9"
                />
              </div>

              {selectedAccounts.length === 0 ? (
                <div className="rounded-xl border border-dashed border-border/60 p-5 text-center">
                  <Building2 className="w-5 h-5 mx-auto text-muted-foreground mb-2" />
                  <p className="text-sm font-medium">No accounts in this workspace</p>
                  <p className="text-xs text-muted-foreground mt-1">
                    Create your first account here so workspace switching has a real destination.
                  </p>
                </div>
              ) : groupedInstitutions.length === 0 ? (
                <div className="rounded-xl border border-dashed border-border/60 p-5 text-center">
                  <Search className="w-5 h-5 mx-auto text-muted-foreground mb-2" />
                  <p className="text-sm font-medium">No matching accounts</p>
                  <p className="text-xs text-muted-foreground mt-1">Try a different institution, account type, or currency search.</p>
                </div>
              ) : (
                <div className="space-y-3 max-h-[44dvh] overflow-y-auto pr-1">
                  {onlyUnassignedInstitutions && (
                    <div className="rounded-xl border border-amber-500/20 bg-amber-500/5 p-3">
                      <p className="text-sm font-medium">Institution setup still missing</p>
                      <p className="mt-1 text-xs text-muted-foreground">
                        These accounts work, but adding institution metadata will make switching clearer across workspaces.
                      </p>
                      <Button variant="outline" size="sm" className="mt-3 gap-2" onClick={() => navigate("/account-activity")}>
                        <Settings2 className="w-3.5 h-3.5" />
                        Add institution details
                      </Button>
                    </div>
                  )}
                  {groupedInstitutions.map((group) => (
                    <div key={group.id} className="rounded-xl border border-border/60">
                      <div className="flex items-center gap-2 border-b border-border/50 px-3 py-2.5 bg-muted/15">
                        <InstitutionLogo logo={group.logo} name={group.name} size={20} />
                        <div className="min-w-0">
                          <p className="text-sm font-medium truncate">{group.name}</p>
                          <p className="text-[11px] text-muted-foreground">{group.accounts.length} account{group.accounts.length === 1 ? "" : "s"}</p>
                        </div>
                      </div>
                      <div className="divide-y divide-border/50">
                        {group.accounts.map((account) => {
                          const isActiveAccount = account.account_id === scope.account_id && effectiveWorkspaceId === scope.workspace_id;
                          const accountTypeConfig = TYPE_CONFIG[account.type] || TYPE_CONFIG.other;
                          const AccountTypeIcon = accountTypeConfig.icon || Building2;
                          return (
                            <button
                              key={account.account_id}
                              onClick={() => handleChooseAccount(effectiveWorkspaceId, account.account_id)}
                              className={`flex w-full items-start gap-3 px-3 py-3 text-left transition-colors hover:bg-muted/30 ${
                                isActiveAccount ? "bg-primary/5" : ""
                              }`}
                              data-testid={`account-option-${account.account_id}`}
                            >
                              <div className="rounded-lg bg-muted/30 p-2">
                                <AccountTypeIcon className="w-4 h-4 text-primary" />
                              </div>
                              <div className="min-w-0 flex-1">
                                <div className="flex items-center gap-2">
                                  <p className="truncate text-sm font-medium">{account.name}</p>
                                  <AccountTypeBadge type={account.type} />
                                </div>
                                <p className="mt-1 text-[11px] text-muted-foreground">
                                  {getInstitutionLabel(account)} · {account.base_currency || "USD"}
                                </p>
                              </div>
                              <div className="shrink-0 flex items-center gap-2">
                                {switching === account.account_id ? (
                                  <Loader2 className="w-3.5 h-3.5 animate-spin text-muted-foreground" />
                                ) : isActiveAccount ? (
                                  <Check className="w-4 h-4 text-primary" />
                                ) : null}
                              </div>
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div className="grid gap-2 sm:grid-cols-2">
              <Button variant="outline" className="gap-2" onClick={() => navigate("/account-activity")}>
                <Settings2 className="w-3.5 h-3.5" />
                Manage accounts
              </Button>
              <Button
                variant="outline"
                className="gap-2"
                onClick={() => setShowTransfer(true)}
                disabled={(selectedAccounts?.length || 0) < 2}
              >
                <ArrowRightLeft className="w-3.5 h-3.5" />
                Transfer / Clone
              </Button>
            </div>
          </div>
        </SheetContent>
      </Sheet>

      <CreateAccountModal
        open={showCreate}
        onClose={() => setShowCreate(false)}
        workspaceId={effectiveWorkspaceId}
        onCreated={handleCreateAccount}
      />
      <TransferHoldingsModal open={showTransfer} onClose={() => setShowTransfer(false)} />
    </>
  );
}
